import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import GameOverModal from "../../../components/GameOverModal";
import GameWonModal from "../../../components/GameWonModal";
import { useDarkMode } from "../../../context/DarkModeProvider";
import { useLanguage } from "context/LanguageProvider";

const HeapSearchGame = () => {
    const [currentLevel, setCurrentLevel] = useState(1);
    const [visitedNodes, setVisitedNodes] = useState([]);
    const [searchComplete, setSearchComplete] = useState(false);
    const [complexitySelected, setComplexitySelected] = useState(false);
    const [selectedComplexity, setSelectedComplexity] = useState(null);
    const [message, setMessage] = useState("");
    const [gameState, setGameState] = useState("playing");
    const [pruningHints, setPruningHints] = useState({});
    const [lives, setLivesLeft] = useState(3);
    const [showGameOver, setShowGameOver] = useState(false);
    const [showGameWon, setShowGameWon] = useState(false);
    const { darkMode } = useDarkMode();
    const { selLanguage, toggleLanguage, isLoading } = useLanguage();

    const levels = [
        {
            type: 'min',
            heap: [3, 7, 8, 11, 14, 12, 9, 13, 15],
            target: 7,
            expectedSteps: 2,
            correctOrder: [3, 7],
            targetExists: true,
        },
        {
            type: 'max',
            heap: [95, 88, 75, 82, 70, 60, 68, 55, 78, 65, 50, 45, 40, 58, 62],
            target: 58,
            expectedSteps: 14,
            correctOrder: [95, 88, 75, 82, 70, 60, 68, 55, 78, 65, 50, 45, 40, 58],
            targetExists: true,
        },
        {
            type: 'min',
            heap: [5, 12, 18, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75],
            target: 28,
            expectedSteps: 15,
            correctOrder: [5, 12, 18, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75],
            targetExists: false,
        }
    ];

    const containerStyle = {
        minHeight: "90vh",
        background: darkMode ? "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #101724ff 100%)" : "var(--lightModeGameBackground)",
        padding: "30px 10px",
        borderRadius: "10px",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        overflowX: "hidden",
    };

    useEffect(() => {
        initializeLevel();
    }, [currentLevel]);

    useEffect(() => {
        if (!message) return;
        const timer = setTimeout(() => {
            setMessage("");
        }, 3000);
        return () => clearTimeout(timer);
    }, [message]);

    const initializeLevel = () => {
        setVisitedNodes([]);
        setSearchComplete(false);
        setComplexitySelected(false);
        setSelectedComplexity(null);
        setMessage("");
        setGameState("playing");
        setPruningHints({});
    };

    const getCurrentLevel = () => levels[currentLevel - 1];

    const visitNode = (index, value) => {
        if (searchComplete || gameState !== "playing") return;
        if (visitedNodes.includes(index)) {
            const newVisited = visitedNodes.filter(idx => idx !== index);
            setVisitedNodes(newVisited);
        } else {
            const newVisitedNodes = [...visitedNodes, index];
            setVisitedNodes(newVisitedNodes);
        }
    };

    const getNodeLevel = (index) => Math.floor(Math.log2(index + 1));

    const getNodePositions = (level, nodeIndex, heapLength, svgWidth = null) => {
        if (!svgWidth) {
            const maxLevel = Math.floor(Math.log2(heapLength));
            const nodesLastLevel = Math.pow(2, maxLevel);
            const minNodeSpacing = 90;
            svgWidth = Math.max(nodesLastLevel * minNodeSpacing, 500);
        }
        const nodesInLevel = Math.pow(2, level);
        const levelSpacing = svgWidth / nodesInLevel;
        const indexInLevel = nodeIndex - (Math.pow(2, level) - 1);
        const startOffset = -(svgWidth / 2) + levelSpacing / 2;
        return {
            x: startOffset + indexInLevel * levelSpacing,
            y: level * 100
        };
    };

    const renderEdges = (heap, svgWidth) => {
        const edges = [];
        for (let i = 0; i < heap.length; i++) {
            const leftChildIndex = 2 * i + 1;
            const rightChildIndex = 2 * i + 2;
            const parentLevel = getNodeLevel(i);
            const parentPos = getNodePositions(parentLevel, i, heap.length, svgWidth);

            if (leftChildIndex < heap.length) {
                const childLevel = getNodeLevel(leftChildIndex);
                const childPos = getNodePositions(childLevel, leftChildIndex, heap.length, svgWidth);
                edges.push(
                    <line
                        key={`edge-${i}-${leftChildIndex}`}
                        x1={parentPos.x}
                        y1={parentPos.y + 30}
                        x2={childPos.x}
                        y2={childPos.y + 30}
                        stroke="#64748b"
                        strokeWidth="3"
                        opacity={gameState === "playing" ? 1 : 0.6}
                    />
                );
            }
            if (rightChildIndex < heap.length) {
                const childLevel = getNodeLevel(rightChildIndex);
                const childPos = getNodePositions(childLevel, rightChildIndex, heap.length, svgWidth);
                edges.push(
                    <line
                        key={`edge-${i}-${rightChildIndex}`}
                        x1={parentPos.x}
                        y1={parentPos.y + 30}
                        x2={childPos.x}
                        y2={childPos.y + 30}
                        stroke="#64748b"
                        strokeWidth="3"
                        opacity={gameState === "playing" ? 1 : 0.6}
                    />
                );
            }
        }
        return edges;
    };

    const renderHeapTree = () => {
        const heap = getCurrentLevel().heap;
        const maxLevel = Math.floor(Math.log2(heap.length));
        const nodesLastLevel = Math.pow(2, maxLevel);
        const minNodeSpacing = 90;
        const svgWidth = Math.max(nodesLastLevel * minNodeSpacing, 500);
        const svgHeight = (maxLevel + 2) * 80;

        return (
            <div style={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
                height: "100%",
                overflowX: "auto",
                marginTop: "30px",
                marginBottom: "30px",
            }}>
                <svg
                    width="110%"
                    height={svgHeight}
                    viewBox={`${-svgWidth / 2} 0 ${svgWidth} ${svgHeight}`}
                    style={{ minWidth: "350px", height: svgHeight, maxWidth: "100%", marginTop: "20px" }}
                    preserveAspectRatio="xMidYMin meet"
                >
                    {renderEdges(heap, svgWidth)}
                    {heap.map((value, index) => {
                        const nodeLevel = getNodeLevel(index);
                        const pos = getNodePositions(nodeLevel, index, heap.length, svgWidth);
                        const isVisited = visitedNodes.includes(index);
                        const visitOrder = visitedNodes.indexOf(index) + 1;

                        return (
                            <g key={index}>
                                <motion.circle
                                    whileHover={gameState === "playing" ? { scale: 1.1 } : {}}
                                    whileTap={gameState === "playing" ? { scale: 0.95 } : {}}
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    onClick={() => {
                                        if (gameState !== "playing") return;
                                        visitNode(index, value);
                                    }}
                                    cx={pos.x}
                                    cy={pos.y + 30}
                                    r="30"
                                    fill={isVisited ? "#0f04a7ff" : '#3b82f6'}
                                    stroke="white"
                                    strokeWidth="3"
                                    style={{
                                        cursor: gameState === "playing" ? "pointer" : "default",
                                        filter: isVisited
                                            ? "drop-shadow(0 6px 15px rgba(255, 107, 107, 0.4))"
                                            : "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
                                        transition: "all 0.3s ease"
                                    }}
                                />
                                <text
                                    x={pos.x}
                                    y={pos.y + 38}
                                    textAnchor="middle"
                                    fill="white"
                                    fontSize="18"
                                    fontWeight="bold"
                                    style={{ pointerEvents: "none" }}
                                >
                                    {value}
                                </text>
                                {isVisited && (
                                    <text
                                        x={pos.x + 22}
                                        y={pos.y + 18}
                                        fontSize="14"
                                        fill="#f11e0eff"
                                        fontWeight="bold"
                                        style={{
                                            textShadow: "0 1px 2px #fff",
                                            pointerEvents: "none"
                                        }}
                                    >
                                        {visitOrder}
                                    </text>
                                )}
                                {pruningHints[index] && (
                                    <foreignObject
                                        x={pos.x - 60}
                                        y={pos.y - 50}
                                        width="120"
                                        height="40"
                                    >
                                        <motion.div
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -10 }}
                                            style={{
                                                background: 'rgba(255, 193, 7, 0.95)',
                                                color: 'black',
                                                padding: '5px 10px',
                                                borderRadius: '10px',
                                                fontSize: '0.8em',
                                                whiteSpace: 'nowrap',
                                                textAlign: 'center'
                                            }}
                                        >
                                            {pruningHints[index]}
                                        </motion.div>
                                    </foreignObject>
                                )}
                            </g>
                        );
                    })}
                    <defs>
                        <linearGradient id="targetGradient" x1="0" y1="0" x2="1" y2="1">
                            <stop offset="0%" stopColor="#FFD700" />
                            <stop offset="100%" stopColor="#FFA500" />
                        </linearGradient>
                    </defs>
                </svg>
            </div>
        );
    };

    const checkSequence = () => {
        const level = getCurrentLevel();
        const visitedValues = visitedNodes.map(index => level.heap[index]);
        const correctValues = level.correctOrder;

        const isCorrectSequence = visitedValues.length === correctValues.length &&
            visitedValues.every((value, idx) => value === correctValues[idx]);

        if (isCorrectSequence) {
            setSearchComplete(true);
            if (level.targetExists) {
                setGameState("found");
                setMessage(selLanguage ? `Άριστα! Βρήκατε το στοιχείο ${level.target} με τη σωστή ακολουθία αναζήτησης σε ${visitedValues.length} βήματα.` : `Excellent! You found the element ${level.target} with the correct search sequence in ${visitedValues.length} steps.`);
            } else {
                setGameState("not_found");
                setMessage(selLanguage ? ` Σωστά! Επισκεφθήκατε όλους τους κόμβους με τη σωστή σειρά και επιβεβαιώσατε ότι το στοιχείο ${level.target} δεν υπάρχει.` : ` Correct! You visited all nodes in the correct order and confirmed that the element ${level.target} does not exist.`);
            }
            setTimeout(() => showComplexitySection(), 1000);
        } else {
            const newLives = lives - 1;
            setLivesLeft(newLives);

            if (newLives <= 0) {
                setShowGameOver(true);
                setGameState("game_over");
            } else {
                setMessage(selLanguage ? "Λάθος ακολουθία! Προσπαθήστε ξανά." : "Wrong sequence! Try again.");
                setTimeout(() => {
                    setVisitedNodes([]);
                }, 2000);
            }
        }
    };

    const showComplexitySection = () => {
        setGameState("complexity");
    };

    const selectComplexity = (complexity) => {
        if (complexitySelected) return;
        setComplexitySelected(true);
        setSelectedComplexity(complexity);
        const isCorrect = complexity === 'O(n)';
        const level = getCurrentLevel();

        if (isCorrect) {
            setMessage(selLanguage
                ? `Σωστά! Η αναζήτηση σε Heap είναι πάντα O(n) γιατί δεν υπάρχει ταξινόμηση μεταξύ των κόμβων. ${level.targetExists
                    ? `Βρήκατε το στοιχείο σε ${visitedNodes.length} βήματα.`
                    : 'Επισκεφθήκατε όλους τους κόμβους για να επιβεβαιώσετε ότι το στοιχείο δεν υπάρχει.'
                }`
                : `Correct! Heap search is always O(n) because there is no ordering between nodes. ${level.targetExists
                    ? `You found the element in ${visitedNodes.length} steps.`
                    : 'You visited all nodes to confirm that the element does not exist.'
                }`
            );
            setGameState("won");
            if (currentLevel === levels.length) {
                setTimeout(() => {
                    setShowGameWon(true);
                }, 1000);
            }
        } else {
            const newLives = lives - 1;
            setLivesLeft(newLives);

            if (newLives <= 0) {
                setShowGameOver(true);
                setGameState("game_over");
            } else {
                setMessage(selLanguage ? `Λάθος! Η σωστή απάντηση είναι O(n). Η αναζήτηση σε Heap απαιτεί γραμμική αναζήτηση.` : `Wrong! The correct answer is O(n). Searching in a Heap requires linear search.`);
                setTimeout(() => {
                    setComplexitySelected(false);
                    setSelectedComplexity(null);
                }, 2000);
            }
        }
    };

    const nextLevel = () => {
        if (currentLevel < levels.length) {
            setCurrentLevel(currentLevel + 1);
        } else {
            setShowGameWon(true);
        }
    };

    const resetGame = () => {
        setCurrentLevel(1);
        setGameState("playing");
        setLivesLeft(3);
        setShowGameOver(false);
        setShowGameWon(false);
        initializeLevel();
    };

    const level = getCurrentLevel();

    return (
        <div style={containerStyle}>
            <GameOverModal
                isVisible={showGameOver}
                onPlayAgain={resetGame}
            />

            <GameWonModal
                isVisible={showGameWon}
                onPlayAgain={resetGame}
            />

            <div className="max-w-7xl mx-auto" style={{ width: "100%" }}>
                <div style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "20px",
                    gap: "15px",
                    minHeight: "50px"
                }}>
                    <div style={{
                        color: darkMode ? "white" : "#1e293b",
                        padding: "12px 24px",
                        borderRadius: "12px",
                        fontWeight: "700",
                        fontSize: "18px",
                        border: darkMode ? "2px solid rgba(255, 255, 255, 0.2)" : "2px solid #1e293b",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px"
                    }}>
                        {selLanguage ? `Επίπεδο: ${currentLevel}/${levels.length}` : `Level: ${currentLevel}/${levels.length}`}
                    </div>

                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        flexDirection: "row",
                        justifyContent: "flex-end",
                        gap: "15px",
                        flexWrap: "wrap",
                        minWidth: "200px"
                    }}>
                        <div style={{
                            display: "flex",
                            gap: "8px",
                            alignItems: "center"
                        }}>
                            {[1, 2, 3].map((heartNum) => (
                                <div
                                    key={heartNum}
                                    style={{
                                        fontSize: "24px",
                                        filter: lives >= heartNum
                                            ? "drop-shadow(0 4px 8px rgba(220, 38, 38, 0.4))"
                                            : "grayscale(100%) opacity(0.3)",
                                        transform: lives >= heartNum ? "scale(1)" : "scale(0.8)",
                                        transition: "all 0.3s ease",
                                        animation: lives >= heartNum ? "heartbeat 2s ease-in-out infinite" : "none"
                                    }}
                                >
                                    ❤️
                                </div>
                            ))}
                        </div>
                    </div>

                    <style>{`
                        @keyframes heartbeat {
                            0%, 100% { transform: scale(1); }
                            50% { transform: scale(1.1); }
                        }
                        
                        @media (max-width: 768px) {
                            .header-container {
                                flex-direction: column;
                                align-items: center;
                                gap: 20px !important;
                            }
                        }
                    `}</style>
                </div>

                <br />

                <div style={{
                    fontSize: '1.2em',
                    marginBottom: '30px',
                    padding: '15px',
                    background: "linear-gradient(135deg, rgba(59, 130, 246, 0.4), rgba(29, 78, 216, 0.4))",
                    borderRadius: '15px',
                    color: "#e6e6e6ff",
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontWeight: '600',
                    gap: '10px'
                }}>
                    {selLanguage ? `Αναζητάτε το στοιχείο: ${level.target}` : `Searching for element: ${level.target}`}
                </div>

                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    marginBottom: '30px',
                    minHeight: '300px'
                }}>
                    {renderHeapTree()}
                </div>

                {gameState === "playing" && visitedNodes.length > 0 && (
                    <motion.button
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        whileHover={{ scale: 1.05, y: -2 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={checkSequence}
                        style={{
                            padding: '15px 30px',
                            background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                            border: 'none',
                            borderRadius: '25px',
                            color: 'white',
                            fontSize: '1.1em',
                            fontWeight: 'bold',
                            cursor: gameState === "playing" ? "pointer" : "default",
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                        }}
                    >
                        {selLanguage ? `Υποβολή (${visitedNodes.length} κόμβοι)` : `Submit (${visitedNodes.length} nodes)`}
                    </motion.button>
                )}

                <AnimatePresence>
                    {gameState === "complexity" && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            style={{
                                margin: '30px 0',
                                padding: '20px',
                                background: 'rgba(255, 255, 255, 0.1)',
                                borderRadius: '15px'
                            }}
                        >
                            <h3 style={{ marginBottom: '15px', color: 'white' }}>
                                {selLanguage ? "Ποια είναι η χρονική πολυπλοκότητα αυτής της αναζήτησης;" : "What is the time complexity of this search?"}
                            </h3>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                gap: '15px',
                                marginTop: '15px',
                                flexWrap: 'wrap'
                            }}>
                                {['O(1)', 'O(log n)', 'O(n)', 'O(n log n)'].map(complexity => (
                                    <motion.button
                                        key={complexity}
                                        whileHover={{ scale: complexitySelected ? 1 : 1.05, y: complexitySelected ? 0 : -2 }}
                                        whileTap={{ scale: complexitySelected ? 1 : 0.95 }}
                                        onClick={() => selectComplexity(complexity)}
                                        disabled={complexitySelected}
                                        style={{
                                            padding: '12px 24px',
                                            background: complexitySelected && selectedComplexity === complexity
                                                ? complexity === 'O(n)'
                                                    ? 'linear-gradient(135deg, #4CAF50, #45a049)'
                                                    : 'linear-gradient(135deg, #FF6B6B, #EE5A5A)'
                                                : complexitySelected && complexity === 'O(n)'
                                                    ? 'linear-gradient(135deg, #4CAF50, #45a049)'
                                                    : 'linear-gradient(135deg, #667eea, #764ba2)',
                                            border: 'none',
                                            borderRadius: '25px',
                                            color: 'white',
                                            fontSize: '1em',
                                            cursor: complexitySelected ? 'default' : 'pointer',
                                            opacity: complexitySelected ? 0.8 : 1,
                                            fontWeight: '600'
                                        }}
                                    >
                                        {complexity}
                                    </motion.button>
                                ))}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                <AnimatePresence>
                    {message && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            style={{
                                fontSize: '1.1em',
                                margin: '20px 0',
                                padding: '15px',
                                borderRadius: '15px',
                                fontWeight: 'bold',
                                color: "#fff",
                                background: (gameState === "won" || gameState === "found" || gameState === "not_found")
                                    ? 'rgba(76, 175, 80, 0.3)'
                                    : 'rgba(244, 67, 54, 0.3)',
                                border: (gameState === "won" || gameState === "found" || gameState === "not_found")
                                    ? '2px solid #4CAF50'
                                    : '2px solid #f44336'
                            }}
                        >
                            {message}
                        </motion.div>
                    )}
                </AnimatePresence>

                {complexitySelected && gameState === "won" && (
                    <motion.button
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        whileHover={{ scale: 1.05, y: -2 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={nextLevel}
                        style={{
                            padding: '15px 30px',
                            background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                            border: 'none',
                            borderRadius: '25px',
                            color: 'white',
                            fontSize: '1.2em',
                            fontWeight: 'bold',
                            cursor: gameState === "playing" ? "pointer" : "default",
                            marginTop: '20px'
                        }}
                    >
                        {currentLevel < levels.length ? selLanguage ? 'Επόμενο Επίπεδο' : "Next Level"
                            : selLanguage ? 'Ολοκλήρωση' : "Finish"}
                    </motion.button>
                )}
            </div>
        </div>
    );
};

export default HeapSearchGame;